import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, TrashIcon, SaveIcon, MinusIcon, MaximizeIcon, MinimizeIcon, ChevronUpIcon, ChevronDownIcon, BookIcon, SearchIcon, XIcon, UploadIcon, FileIcon, FilterIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
import { GLCodingDialog } from '../../GLCodingDialog';
import { SelectedDimensions } from '../financial/gl-coding/types';
import { sampleOrgUnits, flattenOrgUnits } from '../financial/sharedFinancialData';
import { allVendors } from '../vendor/sharedVendorData';
export function InvoiceEntry({
  onSave,
  locale = 'en-US',
  defaultCurrency
}) {
  const [formData, setFormData] = useState({
    number: '',
    invoiceType: '',
    receivedDate: '',
    transactionDate: '',
    note: '',
    currency: defaultCurrency || getDefaultCurrency() || 'AUD',
    exchangeRate: '',
    vendor: {
      id: '',
      name: ''
    },
    buyerOrgUnit: {
      id: '',
      name: ''
    },
    billingOrgUnit: {
      id: '',
      name: ''
    },
    references: {
      purchaseOrderReference: '',
      contractReference: '',
      projectReference: '',
      jobReference: '',
      serviceContractReference: '',
      assetReference: '',
      otherReference: ''
    },
    paymentTerm: {
      statedPaymentTerm: '',
      statedDueDate: '',
      vendorDefaultPaymentTerm: 'Net 30',
      effectivePaymentTerm: 'Net 30',
      effectiveDueDate: '',
      paymentTermsSource: 'Vendor'
    },
    financials: {
      subtotalExcludingTaxAmount: '',
      discountAmount: '',
      taxAmount: '',
      totalExcludingTaxAmount: '',
      totalIncludingTaxAmount: '',
      payableAmount: ''
    },
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: ''
  });
  const [lineItems, setLineItems] = useState([{
    id: 1,
    type: 'Item',
    itemCode: '',
    description: '',
    quantity: 1,
    uom: 'EA',
    unitPrice: 0,
    taxCode: 'STD',
    glCode: '',
    selectedDimensions: {} as Record<string, string>,
    total: 0
  }]);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [imageViewerOpen, setImageViewerOpen] = useState(true);
  const [isDocked, setIsDocked] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [glCodingLineId, setGlCodingLineId] = useState<number | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    vendorOrg: true,
    references: false,
    paymentTerms: false,
    financials: true
  });
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section as keyof typeof expandedSections]
    });
  };
  const invoiceImageUrl = 'https://templates.invoicehome.com/invoice-template-us-neat-750px.png';
  const invoiceTypes = ['Standard', 'Proforma', 'Recurring', 'Prepayment'];
  const vendors = allVendors.map(v => ({
    id: v.id,
    name: v.name,
    code: v.code,
    type: v.type,
    country: v.country?.name || '',
    orgUnitId: v.orgUnitId || ''
  }));
  const orgUnits = flattenOrgUnits(sampleOrgUnits).map(ou => {
    const depth = ou.path.split('/').filter(Boolean).length - 1;
    return {
      id: ou.id,
      name: ou.name,
      code: ou.code,
      path: ou.path,
      depth
    };
  });
  const currencies = [{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'NZD',
    name: 'New Zealand Dollar'
  }];
  const paymentTermsSources = ['Invoice', 'Vendor', 'System'];
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY'];
  const taxCodes = ['STD', 'RED', 'ZERO', 'EXM'];
  const glCodes = [{
    code: '1000',
    name: 'Cash and Cash Equivalents'
  }, {
    code: '2000',
    name: 'Accounts Payable'
  }, {
    code: '5000',
    name: 'Cost of Goods Sold'
  }, {
    code: '6000',
    name: 'Operating Expenses'
  }, {
    code: '6100',
    name: 'Office Supplies'
  }, {
    code: '6200',
    name: 'IT Equipment'
  }];
  const filteredVendors = vendors.filter(v => {
    // Filter by buyer entity (org unit availability)
    if (formData.buyerOrgUnit.id) {
      const selectedOrgUnit = orgUnits.find(ou => ou.id === formData.buyerOrgUnit.id);
      if (selectedOrgUnit) {
        // Vendor has no orgUnitId → global vendor, always available
        if (!v.orgUnitId) {
          // passes org unit filter
        } else {
          // Vendor has orgUnitId → check if buyer entity is same or descendant
          const vendorOrgUnit = orgUnits.find(ou => ou.id === v.orgUnitId);
          if (vendorOrgUnit) {
            // Available if: exact match OR buyer's path starts with vendor's path
            const isMatch = v.orgUnitId === formData.buyerOrgUnit.id;
            const isBuyerDescendant = selectedOrgUnit.path.startsWith(vendorOrgUnit.path + '/');
            const isVendorDescendant = vendorOrgUnit.path.startsWith(selectedOrgUnit.path + '/');
            if (!isMatch && !isBuyerDescendant && !isVendorDescendant) {
              return false;
            }
          } else {
            // Vendor's org unit not found in hierarchy — exclude
            return false;
          }
        }
      }
    }
    // Filter by search term
    if (!vendorSearch) return true;
    const search = vendorSearch.toLowerCase();
    return v.name.toLowerCase().includes(search) || v.code.toLowerCase().includes(search) || v.type.toLowerCase().includes(search) || v.country.toLowerCase().includes(search);
  });
  // Close vendor dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Clear vendor selection when buyer entity changes and vendor is no longer available
  useEffect(() => {
    if (formData.vendor.id && formData.buyerOrgUnit.id) {
      const selectedVendor = vendors.find(v => v.id === formData.vendor.id);
      if (selectedVendor && selectedVendor.orgUnitId) {
        const selectedOrgUnit = orgUnits.find(ou => ou.id === formData.buyerOrgUnit.id);
        const vendorOrgUnit = orgUnits.find(ou => ou.id === selectedVendor.orgUnitId);
        if (selectedOrgUnit && vendorOrgUnit) {
          const isMatch = selectedVendor.orgUnitId === formData.buyerOrgUnit.id;
          const isBuyerDescendant = selectedOrgUnit.path.startsWith(vendorOrgUnit.path + '/');
          const isVendorDescendant = vendorOrgUnit.path.startsWith(selectedOrgUnit.path + '/');
          if (!isMatch && !isBuyerDescendant && !isVendorDescendant) {
            setFormData(prev => ({
              ...prev,
              vendor: {
                id: '',
                name: ''
              }
            }));
          }
        }
      }
    }
  }, [formData.buyerOrgUnit.id]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const val = type === 'checkbox' ? checked : value;
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: val
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: val
      }));
    }
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  const handleObjectSelect = (section: string, id: string, items: any[]) => {
    const selected = items.find(item => item.id === id) || {
      id: '',
      name: ''
    };
    setFormData(prev => ({
      ...prev,
      [section]: selected
    }));
    if (errors[section]) {
      setErrors(prev => ({
        ...prev,
        [section]: null
      }));
    }
  };
  const addLineItem = () => {
    const newId = lineItems.length > 0 ? Math.max(...lineItems.map(item => item.id)) + 1 : 1;
    setLineItems([...lineItems, {
      id: newId,
      type: 'Item',
      itemCode: '',
      description: '',
      quantity: 1,
      uom: 'EA',
      unitPrice: 0,
      taxCode: 'STD',
      glCode: '',
      selectedDimensions: {} as Record<string, string>,
      total: 0
    }]);
  };
  const removeLineItem = (id: number) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };
  const updateLineItem = (id: number, field: string, value: any) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const handleGLCodeApply = (lineId: number, glCode: string, dimensions: SelectedDimensions) => {
    setLineItems(lineItems.map(item => {
      if (item.id === lineId) {
        return {
          ...item,
          glCode,
          selectedDimensions: dimensions
        };
      }
      return item;
    }));
    setGlCodingLineId(null);
  };
  const glCodingLine = glCodingLineId !== null ? lineItems.find(item => item.id === glCodingLineId) : null;
  const handleVendorSelect = (vendor: (typeof vendors)[0]) => {
    setFormData(prev => ({
      ...prev,
      vendor: {
        id: vendor.id,
        name: vendor.name
      }
    }));
    setVendorSearch('');
    setShowVendorDropdown(false);
    if (errors.vendor) {
      setErrors(prev => ({
        ...prev,
        vendor: null
      }));
    }
  };
  const handleClearVendor = () => {
    setFormData(prev => ({
      ...prev,
      vendor: {
        id: '',
        name: ''
      }
    }));
    setVendorSearch('');
  };
  const validateForm = () => {
    const newErrors: Record<string, any> = {};
    if (!formData.number) newErrors.number = 'Invoice number is required';
    if (!formData.receivedDate) newErrors.receivedDate = 'Received date is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (!formData.vendor.id) newErrors.vendor = 'Vendor is required';
    const itemErrors: any[] = [];
    lineItems.forEach((item, index) => {
      const itemError: any = {};
      if (!item.description) itemError.description = 'Description is required';
      if (item.quantity <= 0) itemError.quantity = 'Quantity must be > 0';
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) newErrors.lineItems = itemErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (asDraft = true) => {
    if (!asDraft && !validateForm()) {
      if (errors.number || errors.receivedDate || errors.currency) setExpandedSections(prev => ({
        ...prev,
        header: true
      }));
      if (errors.vendor) setExpandedSections(prev => ({
        ...prev,
        vendorOrg: true
      }));
      return;
    }
    const invoiceData = {
      ...formData,
      lineItems,
      calculatedTotal: calculateTotal()
    };
    onSave(invoiceData, asDraft);
  };
  const toggleImageViewer = () => setImageViewerOpen(!imageViewerOpen);
  const toggleDock = () => setIsDocked(!isDocked);
  const zoomIn = () => setZoomLevel(Math.min(zoomLevel + 10, 200));
  const zoomOut = () => setZoomLevel(Math.max(zoomLevel - 10, 50));
  const handleFileUpload = (file: File) => {
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image file (PNG, JPG, GIF, WebP) or PDF');
      return;
    }
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      setUploadedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };
  const handleRemoveImage = () => {
    setUploadedImage(null);
    setUploadedFileName('');
  };
  return <div className="mb-8" data-id="element-643">
      <div className="flex justify-between items-center mb-6" data-id="element-644">
        <div data-id="element-645">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-646">Invoice Entry</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-647">
            Enter invoice details and line items
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-648">
          <button onClick={toggleImageViewer} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-649">
            {imageViewerOpen ? <MinusIcon className="h-4 w-4 mr-1" data-id="element-650" /> : <PlusIcon className="h-4 w-4 mr-1" data-id="element-651" />}
            {imageViewerOpen ? 'Hide Image' : 'Show Image'}
          </button>
          {imageViewerOpen && <button onClick={toggleDock} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-652">
              {isDocked ? <MaximizeIcon className="h-4 w-4 mr-1" data-id="element-653" /> : <MinimizeIcon className="h-4 w-4 mr-1" data-id="element-654" />}
              {isDocked ? 'Undock' : 'Dock'}
            </button>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6" data-id="element-655">
        <div className="flex flex-col space-y-6" data-id="element-656">
          {/* 1. Header Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-657">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer bg-gray-50" onClick={() => toggleSection('header')} data-id="element-658">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-659">
                Invoice Header
              </h4>
              {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-660" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-661" />}
            </div>
            {expandedSections.header && <div className="px-4 py-5 sm:p-6" data-id="element-662">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-663">
                  <div className="sm:col-span-2" data-id="element-664">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-665">
                      Invoice Number *
                    </label>
                    <input type="text" name="number" value={formData.number} onChange={handleInputChange} className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.number ? 'border-red-300' : ''}`} data-id="element-666" />
                    {errors.number && <p className="mt-1 text-sm text-red-600" data-id="element-667">
                        {errors.number}
                      </p>}
                  </div>

                  <div data-id="element-668">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-669">
                      Received Date *
                    </label>
                    <input type="date" name="receivedDate" value={formData.receivedDate} onChange={handleInputChange} className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.receivedDate ? 'border-red-300' : ''}`} data-id="element-670" />
                    {errors.receivedDate && <p className="mt-1 text-sm text-red-600" data-id="element-671">
                        {errors.receivedDate}
                      </p>}
                  </div>

                  <div data-id="element-672">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-673">
                      Transaction Date
                    </label>
                    <input type="date" name="transactionDate" value={formData.transactionDate} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-674" />
                  </div>

                  <div data-id="element-675">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-676">
                      Currency *
                    </label>
                    <select name="currency" value={formData.currency} onChange={handleInputChange} className={`mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.currency ? 'border-red-300' : ''}`} data-id="element-677">
                      {currencies.map(c => <option key={c.code} value={c.code} data-id="element-678">
                          {c.code} - {c.name}
                        </option>)}
                    </select>
                    {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-679">
                        {errors.currency}
                      </p>}
                  </div>

                  {formData.currency !== 'AUD' && <div data-id="element-680">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-681">
                        Exchange Rate
                      </label>
                      <input type="number" step="0.000001" name="exchangeRate" value={formData.exchangeRate} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="e.g. 0.6500" data-id="element-682" />
                    </div>}
                </div>
              </div>}
          </div>

          {/* 2. Vendor & Organisation */}
          <div className="bg-white shadow sm:rounded-lg overflow-visible" data-id="element-683">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer bg-gray-50 sm:rounded-t-lg" onClick={() => toggleSection('vendorOrg')} data-id="element-684">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-685">
                Vendor & Organisation
              </h4>
              {expandedSections.vendorOrg ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-686" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-687" />}
            </div>
            {expandedSections.vendorOrg && <div className="px-4 py-5 sm:p-6" data-id="element-688">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-689">
                  <div data-id="element-690">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-691">
                      Buyer Entity
                    </label>
                    <select name="buyerOrgUnit" value={formData.buyerOrgUnit.id} onChange={e => handleObjectSelect('buyerOrgUnit', e.target.value, orgUnits)} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-692">
                      <option value="" data-id="element-693">Select Buyer Entity</option>
                      {orgUnits.map(ou => <option key={ou.id} value={ou.id} data-id="element-694">
                          {'\u00A0\u00A0\u00A0\u00A0'.repeat(ou.depth)}
                          {ou.depth > 0 ? '└ ' : ''}
                          {ou.name} ({ou.code})
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-695">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-696">
                      Delivery Entity
                    </label>
                    <select name="billingOrgUnit" value={formData.billingOrgUnit.id} onChange={e => handleObjectSelect('billingOrgUnit', e.target.value, orgUnits)} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-697">
                      <option value="" data-id="element-698">Select Delivery Entity</option>
                      {orgUnits.map(ou => <option key={ou.id} value={ou.id} data-id="element-699">
                          {'\u00A0\u00A0\u00A0\u00A0'.repeat(ou.depth)}
                          {ou.depth > 0 ? '└ ' : ''}
                          {ou.name} ({ou.code})
                        </option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2" data-id="element-700">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-701">
                      Vendor *
                    </label>
                    {formData.buyerOrgUnit.id && <p className="mt-0.5 text-xs text-indigo-600 flex items-center" data-id="element-702">
                        <FilterIcon className="h-3 w-3 mr-1" data-id="element-703" />
                        Filtered by Buyer Entity: {formData.buyerOrgUnit.name} (
                        {filteredVendors.length} of {vendors.length} vendors
                        available)
                      </p>}
                    <div ref={vendorDropdownRef} className="relative mt-1" data-id="element-704">
                      {formData.vendor.id ? <div className={`flex items-center justify-between p-2 border rounded-md bg-white shadow-sm ${errors.vendor ? 'border-red-300' : 'border-gray-300'}`} data-id="element-705">
                          <div className="flex items-center min-w-0" data-id="element-706">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-indigo-100 text-indigo-800 mr-2 flex-shrink-0" data-id="element-707">
                              {vendors.find(v => v.id === formData.vendor.id)?.code}
                            </span>
                            <span className="text-sm text-gray-900 truncate" data-id="element-708">
                              {formData.vendor.name}
                            </span>
                            <span className="text-xs text-gray-500 ml-2 flex-shrink-0" data-id="element-709">
                              {vendors.find(v => v.id === formData.vendor.id)?.type}
                            </span>
                          </div>
                          <button type="button" onClick={handleClearVendor} className="ml-2 p-0.5 text-gray-400 hover:text-gray-600 rounded flex-shrink-0" data-id="element-710">
                            <XIcon className="h-4 w-4" data-id="element-711" />
                          </button>
                        </div> : <div className="relative" data-id="element-712">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-713">
                            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-714" />
                          </div>
                          <input type="text" value={vendorSearch} onChange={e => {
                      setVendorSearch(e.target.value);
                      setShowVendorDropdown(true);
                    }} onFocus={() => setShowVendorDropdown(true)} placeholder="Search by vendor name, code, or type..." className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border ${errors.vendor ? 'border-red-300' : ''}`} data-id="element-715" />
                        </div>}
                      {showVendorDropdown && !formData.vendor.id && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md border border-gray-200 overflow-auto" data-id="element-716">
                          {filteredVendors.length > 0 ? filteredVendors.map(v => <button key={v.id} type="button" onClick={() => handleVendorSelect(v)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between border-b border-gray-100 last:border-b-0" data-id="element-717">
                                <div className="flex items-center min-w-0" data-id="element-718">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono font-medium bg-gray-100 text-gray-700 mr-2 flex-shrink-0" data-id="element-719">
                                    {v.code}
                                  </span>
                                  <span className="text-sm text-gray-900 truncate" data-id="element-720">
                                    {v.name}
                                  </span>
                                </div>
                                <div className="flex items-center ml-2 flex-shrink-0" data-id="element-721">
                                  <span className="text-xs text-gray-500" data-id="element-722">
                                    {v.type}
                                  </span>
                                  <span className="text-xs text-gray-400 ml-2" data-id="element-723">
                                    • {v.country}
                                  </span>
                                </div>
                              </button>) : <div className="px-3 py-3 text-sm text-gray-500 text-center" data-id="element-724">
                              No vendors found matching "{vendorSearch}"
                            </div>}
                        </div>}
                    </div>
                    {errors.vendor && <p className="mt-1 text-sm text-red-600" data-id="element-725">
                        {errors.vendor}
                      </p>}
                  </div>
                </div>
              </div>}
          </div>

          {/* 3. References */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-726">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer bg-gray-50" onClick={() => toggleSection('references')} data-id="element-727">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-728">References</h4>
              {expandedSections.references ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-729" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-730" />}
            </div>
            {expandedSections.references && <div className="px-4 py-5 sm:p-6" data-id="element-731">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-732">
                  {[{
                name: 'references.purchaseOrderReference',
                label: 'Purchase Order Reference'
              }, {
                name: 'references.contractReference',
                label: 'Contract Reference'
              }, {
                name: 'references.projectReference',
                label: 'Project Reference'
              }, {
                name: 'references.jobReference',
                label: 'Job Reference'
              }, {
                name: 'references.serviceContractReference',
                label: 'Service Contract Reference'
              }, {
                name: 'references.assetReference',
                label: 'Asset Reference'
              }, {
                name: 'references.otherReference',
                label: 'Other Reference'
              }].map(field => <div key={field.name} data-id="element-733">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-734">
                        {field.label}
                      </label>
                      <input type="text" name={field.name} value={(formData.references as any)[field.name.split('.')[1]]} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-735" />
                    </div>)}
                </div>
              </div>}
          </div>

          {/* 4. Payment Terms */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-736">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer bg-gray-50" onClick={() => toggleSection('paymentTerms')} data-id="element-737">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-738">
                Payment Terms
              </h4>
              {expandedSections.paymentTerms ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-739" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-740" />}
            </div>
            {expandedSections.paymentTerms && <div className="px-4 py-5 sm:p-6" data-id="element-741">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-742">
                  <div data-id="element-743">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-744">
                      Stated Payment Term
                    </label>
                    <input type="text" name="paymentTerm.statedPaymentTerm" value={formData.paymentTerm.statedPaymentTerm} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-745" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-746">
                      Vendor Default:{' '}
                      {formData.paymentTerm.vendorDefaultPaymentTerm}
                    </p>
                  </div>
                  <div data-id="element-747">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-748">
                      Stated Due Date
                    </label>
                    <input type="date" name="paymentTerm.statedDueDate" value={formData.paymentTerm.statedDueDate} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-749" />
                  </div>
                  <div data-id="element-750">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-751">
                      Effective Payment Term
                    </label>
                    <input type="text" readOnly value={formData.paymentTerm.effectivePaymentTerm} className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md p-2 border bg-gray-50 text-gray-500" data-id="element-752" />
                  </div>
                  <div data-id="element-753">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-754">
                      Effective Due Date
                    </label>
                    <input type="date" readOnly value={formData.paymentTerm.effectiveDueDate} className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md p-2 border bg-gray-50 text-gray-500" data-id="element-755" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-756">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-757">
                      Payment Terms Source
                    </label>
                    <select name="paymentTerm.paymentTermsSource" value={formData.paymentTerm.paymentTermsSource} onChange={handleInputChange} className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-758">
                      {paymentTermsSources.map(s => <option key={s} value={s} data-id="element-759">
                          {s}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>}
          </div>

          {/* 5. Financials */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-760">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer bg-gray-50" onClick={() => toggleSection('financials')} data-id="element-761">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-762">Financials</h4>
              {expandedSections.financials ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-763" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-764" />}
            </div>
            {expandedSections.financials && <div className="px-4 py-5 sm:p-6" data-id="element-765">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-766">
                  {[{
                name: 'financials.subtotalExcludingTaxAmount',
                label: 'Subtotal (Excl. Tax)'
              }, {
                name: 'financials.discountAmount',
                label: 'Discount Amount'
              }, {
                name: 'financials.taxAmount',
                label: 'Tax Amount'
              }, {
                name: 'financials.totalExcludingTaxAmount',
                label: 'Total (Excl. Tax)'
              }, {
                name: 'financials.totalIncludingTaxAmount',
                label: 'Total (Incl. Tax)'
              }, {
                name: 'financials.payableAmount',
                label: 'Payable Amount'
              }].map(field => <div key={field.name} data-id="element-767">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-768">
                        {field.label}
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm" data-id="element-769">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-770">
                          <span className="text-gray-500 sm:text-sm" data-id="element-771">
                            {formData.currency}
                          </span>
                        </div>
                        <input type="number" step="0.01" name={field.name} value={(formData.financials as any)[field.name.split('.')[1]]} onChange={handleInputChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-12 sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-772" />
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Right Column - Invoice Image Viewer */}
        {imageViewerOpen && <div className={`${isDocked ? '' : 'fixed top-20 right-4 z-50 w-2/3 max-w-2xl'}`} data-id="element-773">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-774">
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-775">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-776">
                  Invoice Image
                </h3>
                <div className="flex items-center space-x-2" data-id="element-777">
                  {uploadedImage && <>
                      <button onClick={zoomOut} className="p-1 rounded-md hover:bg-gray-200" title="Zoom Out" data-id="element-778">
                        <MinusIcon className="h-4 w-4" data-id="element-779" />
                      </button>
                      <span className="text-sm" data-id="element-780">{zoomLevel}%</span>
                      <button onClick={zoomIn} className="p-1 rounded-md hover:bg-gray-200" title="Zoom In" data-id="element-781">
                        <PlusIcon className="h-4 w-4" data-id="element-782" />
                      </button>
                      <div className="w-px h-4 bg-gray-300 mx-1" data-id="element-783"></div>
                      <button onClick={() => fileInputRef.current?.click()} className="p-1 rounded-md hover:bg-gray-200 text-gray-600" title="Replace Image" data-id="element-784">
                        <UploadIcon className="h-4 w-4" data-id="element-785" />
                      </button>
                      <button onClick={handleRemoveImage} className="p-1 rounded-md hover:bg-red-100 text-red-500" title="Remove Image" data-id="element-786">
                        <TrashIcon className="h-4 w-4" data-id="element-787" />
                      </button>
                    </>}
                </div>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileInputChange} className="hidden" data-id="element-788" />

              {uploadedImage ? <div data-id="element-789">
                  {uploadedFileName && <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center" data-id="element-790">
                      <FileIcon className="h-3.5 w-3.5 text-indigo-500 mr-1.5 flex-shrink-0" data-id="element-791" />
                      <span className="text-xs text-indigo-700 truncate" data-id="element-792">
                        {uploadedFileName}
                      </span>
                    </div>}
                  <div className="p-4 overflow-auto" style={{
              maxHeight: '80vh'
            }} data-id="element-793">
                    {uploadedImage.startsWith('data:application/pdf') ? <iframe src={uploadedImage} title="PDF Preview" className="w-full border-0" style={{
                height: '70vh',
                minHeight: '500px'
              }} data-id="element-794" /> : <img src={uploadedImage} alt="Invoice" className="w-full h-auto" data-id="element-795" />}
                  </div>
                </div> : <div className={`p-6 ${isDragging ? 'bg-indigo-50' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-796">
                  <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'}`} onClick={() => fileInputRef.current?.click()} data-id="element-797">
                    <UploadIcon className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`} data-id="element-798" />
                    <p className="text-sm font-medium text-gray-700" data-id="element-799">
                      {isDragging ? 'Drop your file here' : 'Upload Invoice Image'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-800">
                      Drag & drop or click to browse
                    </p>
                    <p className="text-xs text-gray-400 mt-2" data-id="element-801">
                      Supports PNG, JPG, GIF, WebP, PDF
                    </p>
                  </div>
                </div>}
            </div>
          </div>}
      </div>

      {/* Line Items (Full Width) */}
      <div className="mt-6" data-id="element-802">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-803">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-804">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-805">
              Line Items
            </h3>
            <button type="button" onClick={addLineItem} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-806">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-807" />
              Add Line Item
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-808">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-809">
              <thead className="bg-gray-50" data-id="element-810">
                <tr data-id="element-811">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-812">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-813">
                    Item Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-814">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-815">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-816">
                    UOM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-817">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-818">
                    Tax Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-819">
                    GL Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-820">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-821">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-822">
                {lineItems.map(item => <tr key={item.id} data-id="element-823">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-824">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.type} onChange={e => updateLineItem(item.id, 'type', e.target.value)} data-id="element-825">
                        <option value="Item" data-id="element-826">Item</option>
                        <option value="Service" data-id="element-827">Service</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-828">
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.itemCode} onChange={e => updateLineItem(item.id, 'itemCode', e.target.value)} placeholder="Code" data-id="element-829" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-830">
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)} placeholder="Desc" data-id="element-831" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-832">
                      <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-20 sm:text-sm border-gray-300 rounded-md" value={item.quantity} onChange={e => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 0)} min="0" step="1" data-id="element-833" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-834">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.uom} onChange={e => updateLineItem(item.id, 'uom', e.target.value)} data-id="element-835">
                        {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-836">
                            {uom}
                          </option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-837">
                      <div className="relative rounded-md shadow-sm" data-id="element-838">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-839">
                          <span className="text-gray-500 sm:text-sm" data-id="element-840">
                            {formData.currency}
                          </span>
                        </div>
                        <input type="text" value={item.unitPrice} onChange={e => {
                      const sanitizedValue = e.target.value.replace(/[^\d.]/g, '');
                      updateLineItem(item.id, 'unitPrice', parseFloat(sanitizedValue) || 0);
                    }} className="block w-28 pl-10 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-right" placeholder="0.00" data-id="element-841" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-842">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.taxCode} onChange={e => updateLineItem(item.id, 'taxCode', e.target.value)} data-id="element-843">
                        {taxCodes.map(code => <option key={code} value={code} data-id="element-844">
                            {code}
                          </option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-845">
                      <button type="button" onClick={() => setGlCodingLineId(item.id)} className={`inline-flex items-center px-3 py-1.5 border rounded-md shadow-sm text-sm leading-4 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${item.glCode ? 'border-green-300 text-green-800 bg-green-50 hover:bg-green-100' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-846">
                        <BookIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-847" />
                        {item.glCode || 'Set GL Code'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-848">
                      {formatCurrency(item.quantity * item.unitPrice, formData.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-849">
                      <button onClick={() => removeLineItem(item.id)} className="text-red-600 hover:text-red-900" data-id="element-850">
                        <TrashIcon className="h-5 w-5" data-id="element-851" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-852">
                <tr data-id="element-853">
                  <td colSpan={8} className="px-6 py-4 text-right text-sm font-medium text-gray-900" data-id="element-854">
                    Calculated Total:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-855">
                    {formatCurrency(calculateTotal(), formData.currency, locale)}
                  </td>
                  <td data-id="element-856"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* GL Coding Dialog */}
      {glCodingLine && <GLCodingDialog lineItem={{
      id: glCodingLine.id,
      description: glCodingLine.description || `Line ${glCodingLine.id}`,
      glCode: glCodingLine.glCode,
      selectedDimensions: glCodingLine.selectedDimensions || undefined,
      vendor: formData.vendor.id ? {
        id: formData.vendor.id,
        name: formData.vendor.name
      } : undefined,
      dimensionCount: 5
    }} onApply={(glCode, dimensions) => handleGLCodeApply(glCodingLine.id, glCode, dimensions)} onClose={() => setGlCodingLineId(null)} data-id="element-857" />}

      {/* Notes Section */}
      <div className="mt-6" data-id="element-858">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-859">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-860">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-861">
              Notes
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-862">
            <StandardizedNotes value={formData.note} onChange={value => setFormData({
            ...formData,
            note: value
          })} visibility={formData.noteVisibility} onVisibilityChange={visibility => setFormData({
            ...formData,
            noteVisibility: visibility
          })} visibilityDetails={formData.noteVisibilityDetails} onVisibilityDetailsChange={details => setFormData({
            ...formData,
            noteVisibilityDetails: details
          })} label="Invoice Notes" placeholder="Add any notes about this invoice. Select who can view these notes using the visibility options." data-id="element-863" />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6" data-id="element-864">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-865">
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-3" data-id="element-866">
            <button type="button" onClick={() => handleSubmit(true)} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-867">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-868" />
              Save as Draft
            </button>
            <button type="button" onClick={() => handleSubmit(false)} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-869">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-870" />
              Submit Invoice
            </button>
          </div>
        </div>
      </div>
    </div>;
}